import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { max, min } from 'lodash';

import type CurrentDomain from '@/core/lib/new-architecture/domain/current.domain';
import type DonationDomain from '@/core/lib/new-architecture/domain/donation.domain';
import type { UsersPrivateProfilResult, UsersPublicProfilResult } from '@/core/types/profile';

interface UserDomainPublicInfo {
  avatarUrl: string;
  username: string;
  donationDonated: number;
  donationReceived: number;
  city: string | null;
  createdAt: Dayjs;
  isNice: boolean;
  isPunctual: boolean;
}

interface UserDomainPrivateInfo {
  email: string;
}

export enum UserActionType {
  REPORT = 'report',
  BLOCK = 'block',
  UNBLOCK = 'unblock',
  SHARE = 'share',
}

export type ReportAction = {
  action: UserActionType.REPORT;
  uuid: string;
  text: string;
  disabled: boolean;
};

export type BlockAction = {
  action: UserActionType.BLOCK;
  uuid: string;
  text: string;
};

export type UnblockAction = {
  action: UserActionType.UNBLOCK;
  uuid: string;
  text: string;
  disabled: boolean;
};

export type ShareAction = {
  action: UserActionType.SHARE;
  uuid: string;
  text: string;
};

export type UserAction = ReportAction | BlockAction | UnblockAction | ShareAction;

export interface UserDomainInterface {
  uuid: string;
  getPublicInfo: () => Omit<UserDomainPublicInfo, 'isNice' | 'isPunctual'>;
  getPrivateInfo: () => UserDomainPrivateInfo | null;
  getActions: () => UserAction[];
  getDonations: () => DonationDomain[];
  isBlocked: () => boolean;
  isPrivate: () => boolean;

  setDonations: (donations: DonationDomain[]) => void;
}

interface UserDomainConstructor {
  data: UsersPublicProfilResult | UsersPrivateProfilResult;
  current: CurrentDomain | null;
  donations?: DonationDomain[];
}

export type BlockedUserReason = null | 'admin' | 'current-user';

class UserDomain implements UserDomainInterface {
  data: UsersPublicProfilResult | UsersPrivateProfilResult;

  // temporary
  public publicUser: UsersPublicProfilResult | undefined = undefined;

  public privateUser: UsersPrivateProfilResult | undefined = undefined;

  private current: CurrentDomain | null;

  private donations: DonationDomain[];

  constructor({ data, current, donations }: UserDomainConstructor) {
    this.data = data;

    // temporary
    this.publicUser = this.data;
    this.privateUser = 'has_password' in this.data ? this.data : undefined;

    this.current = current;
    this.donations = donations ?? [];
  }

  get uuid() {
    return this.data.uuid;
  }

  get isFollowed() {
    return this.current?.followingUsers.find(user => user.uuid === this.data.uuid);
  }

  get totalCo2() {
    return this.data.co2_given + this.data.co2_received;
  }

  get totalCo2Formatted() {
    const formattedTotal = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(this.totalCo2);
    return `${formattedTotal} kg`;
  }

  get totalWeight() {
    return this.data.weight_given + this.data.weight_received;
  }

  get activityPoints() {
    const { activity_points, activity_exp_soon, super_user_threshold } = this.data;

    const points = {
      all: activity_points,
      expSoon: activity_exp_soon,
      notExpSoon: activity_points - activity_exp_soon,
      superUserThreshold: super_user_threshold,
      missing: max([0, super_user_threshold - activity_points]),
    };
    const allPercent = Number(Number((points.all * 100) / points.superUserThreshold).toFixed());
    const notExpSoonPercent = ((points.all - points.expSoon) * 100) / points.superUserThreshold;
    return {
      points,
      percent: {
        all: min([allPercent, 100]),
        expSoon: notExpSoonPercent >= 100 ? 0 : (points.expSoon * 100) / points.superUserThreshold,
        notExpSoon: min([notExpSoonPercent, 100]),
      },
    };
  }

  get status() {
    const { is_nice, is_punctual } = this.data;
    return {
      isSuperUser: this.activityPoints.points.missing === 0,
      isNice: is_nice,
      isPunctual: is_punctual,
    };
  }

  public getPublicInfo() {
    return {
      avatarUrl: this.data.avatar_url,
      username: this.data.username,
      donationDonated: this.data.donation_donated,
      donationReceived: this.data.donation_received,
      city: this.getCity(),
      createdAt: dayjs(this.data.created_at),
      signature: this.data.signature,
      followersCount: this.data.nb_followers ?? 0,
    };
  }

  public getPrivateInfo() {
    if ('has_password' in this.data) {
      return {
        email: this.data.email,
      };
    }

    return null;
  }

  public getActions() {
    const report = {
      action: UserActionType.REPORT,
      uuid: this.uuid,
      text: 'options.report',
      disabled: this.blockedReason === 'admin',
    } satisfies ReportAction;

    const block = {
      action: UserActionType.BLOCK,
      uuid: this.uuid,
      text: 'options.block',
    } satisfies BlockAction;

    const unblock = {
      action: UserActionType.UNBLOCK,
      uuid: this.uuid,
      text: 'options.unblock',
      disabled: this.blockedReason === 'admin',
    } satisfies UnblockAction;

    // it means it is our profile and we can only share it
    if ('has_password' in this.data) {
      return [];
    }

    // since we have to be connected the actions block and report
    // are for now excluded
    if (!this.current) {
      return [];
    }

    return [report, this.isBlocked() ? unblock : block];
  }

  public getDonations() {
    return this.donations;
  }

  get blockedReason(): BlockedUserReason {
    return !this.isBlocked() ? null : this.data.state === 'blocked' ? 'admin' : 'current-user';
  }

  public isBlocked() {
    const isBlockedByAdmin = this.data.state === 'blocked';
    const isBlockedByCurrent = !!this.current?.hasBlocked(this.uuid);
    return isBlockedByAdmin || isBlockedByCurrent;
  }

  public isPrivate() {
    return !!this.privateUser || this.current?.uuid === this.data.uuid;
  }

  public setDonations(donations: DonationDomain[]) {
    this.donations = donations;
  }

  private getCity(): string | null {
    if (this.data.sub) {
      const userSub = this.data.subs?.find(sub => sub.id === this.data.sub);

      if (userSub) {
        return userSub.name;
      }
    }

    const userCity = this.data.cities?.find(city => city.id === this.data.city);
    const userAdmin = this.data.admins?.find(admin => admin.id === userCity?.admin);

    if (userCity) {
      if (userAdmin) {
        return `${userCity.name} (${userAdmin.name})`;
      }

      return userCity.name;
    }

    return null;
  }
}

export default UserDomain;
